import { PerchLogos } from '@perch-labs/shrike';
import { useCallback, useEffect, useRef } from 'react';
import { Button } from '~/components/button';
import { Utility } from '~/components/dropdowns';
import { CountyForZip } from '~/components/dropdowns/county-for-zip/county-for-zip.component';
import { Form } from '~/components/form';
import {
  type ImageType,
  SELECT_UTILITY_IMAGE,
  ImageSelector,
  ARCADIA_LOGO,
} from '~/components/image';
import { Inputs } from '~/components/inputs';
import { Container, TwoColumnGrid } from '~/components/layout';
import { Description, Title } from '~/components/typography';
import { OnChange } from '~/components/validating-input';
import { useCopy, useForm } from '~/hooks';
import { ORGANIZATION_ENTITIES } from '~/utils/constants';
import {
  LogoSeparator,
  MergerBanner,
  MergerBannerContent,
  MergerBannerHeading,
  MergerBannerLogos,
  MergerBannerSubtext,
  MergerBannerWrapper,
  StyledRadioGroup,
} from './select-utility.styles';

export const SelectUtility = ({ context, next, overrideCopy }: SelectUtilityProps) => {
  const copy = useCopy(context, defaultCopy, overrideCopy);
  const showCountySelection = context.flags?.showCountySelection;
  const [form, updateForm, isValid] = useForm({
    utilityId: context?.utilityId || '',
    zipCode: context?.zipCode || '',
    customerType: context?.customerType || undefined,
    ...(showCountySelection
      ? {
          countyId: context.countyId,
          countyName: context.countyName || '',
        }
      : {}),
  });
  const handleCustomerTypeChange = selection =>
    updateForm(selection, { name: 'customerType', isValid: true });
  const shouldShowCustomerTypeSelection = context.flags?.showCustomerTypeSelection;
  const showMergerBanner = context.signupSource === ORGANIZATION_ENTITIES.ARCADIA.toLowerCase();
  const submitButtonDisabled = shouldShowCustomerTypeSelection
    ? !isValid
    : !form.utilityId || !form.zipCode || (!!showCountySelection && !isValid);
  const isFormValid = !submitButtonDisabled;

  useEffect(() => {
    if (context?.customerType) {
      updateForm(context.customerType, { name: 'customerType', isValid: true });
      return;
    }
    if (!shouldShowCustomerTypeSelection) {
      updateForm(undefined, { name: 'customerType', isValid: true });
    }
  }, [context?.customerType, shouldShowCustomerTypeSelection, updateForm]);

  const customerTypeOptions = useCallback(() => {
    const customerTypes: CustomerTypeOptionsMap = {
      Residential: 'Home',
      Business: 'Business',
    };
    return Object.keys(customerTypes).map(customerType => {
      return {
        children: <p key={customerType}>{customerTypes[customerType]}</p>,
        value: customerType,
      };
    });
  }, []);

  const zipCodeRef = useRef(form.zipCode);
  const handleZipCodeChange: OnChange = (value, { isValid }) => {
    updateForm(value, { name: 'zipCode', isValid });
    if (value !== zipCodeRef.current) {
      zipCodeRef.current = value;
      updateForm(0, { name: 'utilityId', isValid: false });
    }
  };

  return (
    <TwoColumnGrid testId="screen-SelectUtility">
      {showMergerBanner ? (
        <MergerBannerWrapper>
          <MergerBanner data-testid="merger-banner">
            <MergerBannerLogos>
              <PerchLogos type="default" width={120} height={40} />
              <LogoSeparator aria-hidden="true">+</LogoSeparator>
              <ImageSelector type={ARCADIA_LOGO} />
            </MergerBannerLogos>
            <MergerBannerContent>
              <MergerBannerHeading>{copy.mergerBannerHeading}</MergerBannerHeading>
              <MergerBannerSubtext>{copy.mergerBannerSubtext}</MergerBannerSubtext>
            </MergerBannerContent>
          </MergerBanner>
        </MergerBannerWrapper>
      ) : null}
      <TwoColumnGrid.Primary>
        <Title>{copy.title}</Title>
        <Description mb={0}>{copy.description}</Description>
        {!!copy.descriptionTwo && (
          <Description mb={0} mt={5}>
            {copy.descriptionTwo}
          </Description>
        )}
        <Form
          isValid={isFormValid}
          onSubmit={() => {
            const { countyId, countyName, ...rest } = form;
            next({
              ...rest,
              ...(countyId ? { countyId: Number(countyId), countyName } : {}),
            } as SelectUtilityForm);
          }}
        >
          <Container addInputMargin marginY={7}>
            {shouldShowCustomerTypeSelection ? (
              <StyledRadioGroup
                legend="Property type"
                margin="0 0 0 0"
                name="customer-type"
                options={customerTypeOptions()}
                isChecked={(_, value) => value === form.customerType}
                onChange={handleCustomerTypeChange}
                width="initial"
              />
            ) : null}
            <Inputs.ZipCode
              label={copy.zipCodeLabel}
              placeholder={copy.zipCodePlaceholder}
              onChange={handleZipCodeChange}
              value={form.zipCode}
            />
            <CountyForZip
              enabled={!!showCountySelection}
              zipCode={form.zipCode}
              onChange={updateForm}
              value={typeof form.countyId === 'number' ? form.countyId : undefined}
            />
            <Utility onChange={updateForm} value={Number(form.utilityId)} zipCode={form.zipCode} />
          </Container>
          <Button disabled={submitButtonDisabled} type="submit" mode="primary">
            {copy.next}
          </Button>
        </Form>
      </TwoColumnGrid.Primary>
      <TwoColumnGrid.Secondary>
        <ImageSelector type={copy.imageType} />
      </TwoColumnGrid.Secondary>
    </TwoColumnGrid>
  );
};

export const defaultCopy = () => ({
  title: 'Check if community solar is available in your zip code.',
  description: 'Enter your zip code and your utility to check availability.',
  descriptionTwo: '',
  imageType: SELECT_UTILITY_IMAGE as ImageType,
  mergerBannerHeading: 'Arcadia Community Solar is now part of Perch Energy',
  mergerBannerSubtext:
    'Arcadia and Perch have helped thousands of renters, homeowners, and businesses benefit from community solar.',
  next: 'Continue',
  zipCodeLabel: 'Zip code',
  zipCodePlaceholder: 'Enter your zip code',
});

export type SelectUtilityCopy = ReturnType<typeof defaultCopy>;

export interface SelectUtilityProps {
  context: SelectUtilityContext;
  next: (payload: SelectUtilityForm) => void;
  overrideCopy?: (context: SelectUtilityContext) => Partial<SelectUtilityCopy>;
}

export interface SelectUtilityForm {
  utilityId: number;
  zipCode: string;
  customerType?: string;
  countyId?: number;
  countyName?: string;
}

export interface SelectUtilityContext {
  utilityId?: number;
  zipCode?: string;
  signupSource?: string;
  flags?: {
    showCustomerTypeSelection?: boolean;
    showCountySelection?: boolean;
  };
  customerType?: string;
  countyId?: number;
  countyName?: string;
}

export type CustomerTypes = 'Residential' | 'Business';

export type CustomerOptionValues = 'Home' | 'Business';

export type CustomerTypeOptionsMap = {
  [K in CustomerTypes]: CustomerOptionValues;
};
